import { useRef, useState } from "react";
import { slugify } from "../../shared/slug";
import { imageUrl, uploadImageFile } from "../api";

export interface ProductDraft {
  name: string;
  slug: string;
  description: string;
  brandColor: string;
  logoKey: string | null;
}

export const emptyProduct: ProductDraft = { name: "", slug: "", description: "", brandColor: "#4F46E5", logoKey: null };

/** Product fields. Slug follows the name until the user edits it directly. */
export default function ProductForm({ value, onChange, slugLocked = false }: { value: ProductDraft; onChange: (v: ProductDraft) => void; slugLocked?: boolean }) {
  const [slugTouched, setSlugTouched] = useState(slugLocked);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const upload = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true);
    setErr(null);
    try {
      const r = await uploadImageFile(f);
      onChange({ ...value, logoKey: r.key });
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="stack">
      <div className="grid-2">
        <label className="field">
          <span>Product name</span>
          <input type="text" value={value.name} placeholder="Sophos" onChange={(e) => onChange({ ...value, name: e.target.value, slug: slugTouched ? value.slug : slugify(e.target.value) })} />
        </label>
        <label className="field">
          <span>URL name <span className="hint">demos.ironhullsystems.com/{value.slug || "…"}/…</span></span>
          <input type="text" value={value.slug} onChange={(e) => { setSlugTouched(true); onChange({ ...value, slug: slugify(e.target.value) }); }} />
        </label>
      </div>
      <label className="field">
        <span>Description <span className="hint">Only visible to you</span></span>
        <textarea value={value.description} onChange={(e) => onChange({ ...value, description: e.target.value })} />
      </label>
      <div className="row" style={{ alignItems: "flex-end", gap: 24 }}>
        <label className="field">
          <span>Brand color <span className="hint">Hotspots, progress bar and buttons</span></span>
          <div className="row">
            <input type="color" value={value.brandColor} onChange={(e) => onChange({ ...value, brandColor: e.target.value.toUpperCase() })} />
            <code className="small">{value.brandColor}</code>
          </div>
        </label>
        <div className="field">
          <span>Logo <span className="hint">Shown in the player header</span></span>
          <div className="row">
            {value.logoKey && <img src={imageUrl(value.logoKey)} alt="" style={{ height: 32, background: "#fff", padding: 3, border: "1px solid var(--line)", borderRadius: 4 }} />}
            <button type="button" className="btn btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? "Uploading…" : value.logoKey ? "Replace logo" : "Upload logo"}
            </button>
            {value.logoKey && <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange({ ...value, logoKey: null })}>Remove</button>}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(e) => upload(e.target.files?.[0])} />
          </div>
        </div>
      </div>
      {err && <div className="error-box">{err}</div>}
    </div>
  );
}
